import { OptionsType, Radio, useModal } from '@europrocurement/flexy-components';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import {
    ApiDossiersIdActionPutActionEnum,
    ApiDossiersIdTransitionsGetCollection200Response,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme,
    lighten,
    FormControl,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import useTransition from '../../../hooks/useTransition';

type DossierFormValidationStatusModalProps = {
    dossier: DossierOpenSearch;
    withoutSaveForm: boolean;
    handleSubmit: (actionIdentifier: 'save' | ApiDossiersIdActionPutActionEnum) => Promise<void>;
};
const DossierFormValidationStatusModal: React.FunctionComponent<DossierFormValidationStatusModalProps> =
    function ({ dossier, withoutSaveForm = false, handleSubmit }) {
        const { enqueueSnackbar } = useSnackbar();
        const { modalActions } = useModal();
        const { palette } = useTheme();
        const { getTransitionStatusAvailable } = useTransition();
        const [processing, setProcessing] = useState(false);
        const [loading, setLoading] = useState(true);

        const defaultStatusOptions = [
            {
                value: 'save',
                label: withoutSaveForm
                    ? 'Rester sur le statut actuel'
                    : 'Sauvegarder (rester sur le statut actuel)',
            },
        ];
        const [statusOptions, setStatusOptions] = useState<OptionsType>(defaultStatusOptions);
        const [selectedStatusOption, setSelectedStatusOption] = useState<
            ApiDossiersIdActionPutActionEnum | 'save'
        >('save');

        const handleClose = () => {
            modalActions.close();
        };

        useEffect(() => {
            if (!dossier) {
                return;
            }
            setProcessing(false);
            getTransitionStatusAvailable(dossier)
                .then((res: ApiDossiersIdTransitionsGetCollection200Response) => {
                    if (res['hydra:member']) {
                        const statusOptionsDb = res['hydra:member'].map((transition) => ({
                            value: transition.identifier as string,
                            label: transition.target?.libelle as string,
                        }));
                        setStatusOptions([...defaultStatusOptions, ...statusOptionsDb]);
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération des status disponibles',
                        { variant: 'error' },
                    );
                    console.log(err);
                    setStatusOptions(defaultStatusOptions);
                })
                .finally(() => setLoading(false));

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dossier]);

        const handleValidate = async () => {
            setProcessing(true);
            handleSubmit(selectedStatusOption);
        };

        const btnSx = {
            padding: '16px 24px',
            minWidth: 'auto',
            lineHeight: '24px',
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {loading ? (
                    <Box
                        sx={{
                            width: '450px',
                            height: '300px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress
                            color="inherit"
                            size="24px"
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="l2dh6">
                            Souhaitez-vous modifier le statut de ce dossier ?
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="caption">
                                La modification du statut impactera la progression du dossier.
                            </Typography>
                            <Typography variant="caption">
                                Veuillez sélectionner l&apos;option souhaitée.
                            </Typography>
                        </Box>

                        <Box>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label="status"
                                    name="status"
                                    value={selectedStatusOption}
                                    onChange={(event) =>
                                        setSelectedStatusOption(
                                            event.target.value as
                                                | ApiDossiersIdActionPutActionEnum
                                                | 'save',
                                        )
                                    }
                                    sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                >
                                    {statusOptions.map((status) => (
                                        <FormControlLabel
                                            key={`status_${status.value}`}
                                            value={status.value}
                                            control={<Radio />}
                                            label={status.label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                columnGap: '16px',
                            }}
                        >
                            <Button
                                variant="text"
                                sx={{
                                    padding: '16px 24px',
                                    alignItems: 'center',
                                    color: 'primary.dark',
                                }}
                                onClick={() => handleClose()}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    ...btnSx,
                                    backgroundColor: palette.primary.dark,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0 10px',
                                    '&:hover': {
                                        backgroundColor: lighten(palette.primary.dark, 0.2),
                                    },
                                }}
                                onClick={handleValidate}
                                disabled={processing}
                            >
                                {processing && (
                                    <CircularProgress
                                        color="inherit"
                                        size="16px"
                                    />
                                )}
                                Confirmer la modification
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

export default DossierFormValidationStatusModal;
